import React, { useState, useEffect, useContext, useMemo } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import { StateContext, DispatchContext } from "../../store/context";
import { UPDATE_SELECTED_MARKET_ID } from "../../store/types";
import { TitleArea } from "../../components/Typography/titleArea";
import { usePreventCopy } from "../../hooks/usePreventCopy";
import Heatmap from "./components/Heatmap";
import DisclaimerButton from "../../components/DisclaimerButton";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./marketHeatmap.theme.css";

const ALL_SECTORS = "all";

const StockHeatmapScreen: React.FC = (): React.ReactElement => {
  usePreventCopy();

  const { selectedMarketId, baseCurrency } = useContext(StateContext);

  const dispatch = useContext(DispatchContext);

  const [heatmapStocks, setHeatmapStocks] = useState<any[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<number | string>(
    selectedMarketId,
  );
  const [selectedSector, setSelectedSector] = useState<string>(ALL_SECTORS);

  const {
    response: stockMarkets,
    loading: stockMarketLoading,
    execute: getStockMarkets,
  } = useFetchApi("/api/stock-markets", {}, "GET");

  const {
    response: stocks,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/stocks");

  useEffect(() => {
    getStockMarkets();
  }, []);

  useEffect(() => {
    if (!stockMarketLoading && stockMarkets?.length) {
      const marketId = selectedMarket || stockMarkets[0].id;
      setSelectedMarket(marketId);
      fetchStocks(marketId);
    }
  }, [stockMarkets, stockMarketLoading]);

  useEffect(() => {
    if (!stocks) return;

    const raw: any[] = stocks?.data ?? (Array.isArray(stocks) ? stocks : []);

    const mapped = raw.map((s: any) => ({
      symbol: s.ticker_symbol ?? s.security_name,
      change: s.change_perc ?? 0,
      company_name: s.company_name,
      closed: s.closed,
      volume: s.volume,
      total_trades: s.total_trades,
      open: s.open,
      high: s.high,
      low: s.low,
      sector_id: s.sector_id,
      sector_name: s.sector_name,
      last_traded_date: s.last_traded_date,
      marketCap: s.market_cap ?? 0,
      company_logo: s.company_logo,
    }));

    setHeatmapStocks(mapped);
    setSelectedSector(ALL_SECTORS);
  }, [stocks]);

  const sectors = useMemo(() => {
    const seen = new Map<number, string>();
    heatmapStocks.forEach((s) => {
      if (s.sector_id && s.sector_name && !seen.has(s.sector_id)) {
        seen.set(s.sector_id, s.sector_name);
      }
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [heatmapStocks]);

  const filteredStocks = useMemo(() => {
    if (selectedSector === ALL_SECTORS) return heatmapStocks;
    return heatmapStocks.filter(
      (s) => String(s.sector_id) === String(selectedSector),
    );
  }, [heatmapStocks, selectedSector]);

  const fetchStocks = (marketId: any) => {
    getStocks({ marketId, asMap: false, baseCurrency: baseCurrency });
  };

  const handleMarketSelection = (marketId: string | number) => {
    setSelectedMarket(marketId);
    dispatch({ type: UPDATE_SELECTED_MARKET_ID, payload: marketId });
    fetchStocks(marketId);
  };

  const handleSectorSelection = (sectorId: string) => {
    setSelectedSector(sectorId);
  };

  return (
    <div className="vi-hm flex md:flex-row flex-col items-center md:items-start w-full select-none">
      <div className="flex flex-col w-full h-full items-start px-4 py-3">
        <div className="w-full flex items-start justify-between gap-4">
          <TitleArea
            variant="page"
            title="Heatmap"
            subTitle="Visualise market momentum and sector wise performance at a glance"
          />
          <DisclaimerButton />
        </div>
        <hr className="hr-line w-full my-2" />

        <div className="w-full">
          <Heatmap
            stocks={filteredStocks.length ? filteredStocks : undefined}
            height={700}
            markets={stockMarkets}
            marketsLoading={stockMarketLoading}
            selectedMarket={selectedMarket}
            onMarketChange={handleMarketSelection}
            sectors={sectors}
            stocksLoading={stockLoading}
            selectedSector={selectedSector}
            onSectorChange={handleSectorSelection}
          />
        </div>
      </div>
    </div>
  );
};

export default StockHeatmapScreen;
